import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Lock, Pencil, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { Toolbar } from '@/shared/components/custom/toolbar';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useTimeOffTypes } from '../../hooks/useTimeOffTypes';
import { useDeleteTimeOffType } from '../../hooks/useDeleteTimeOffType';
import { TypeFormDialog } from './TypeFormDialog';
import type { TimeOffTypeDto } from '../../types';

/** Joins only the behavior flags that are actually true — there's no "paid/unpaid" or balance-policy field on the DTO to describe otherwise. */
function buildFactsLine(type: TimeOffTypeDto, t: TFunction<'timeOff'>) {
  const facts: string[] = [];
  if (type.requiresApproval) facts.push(t('settings.types.requiresApproval'));
  if (type.consumesVacationBalance) facts.push(t('settings.types.consumesBalance'));
  if (type.allowsHourly) facts.push(t('settings.types.allowsHourly'));
  if (type.requiresAttachment) facts.push(t('settings.types.requiresAttachment'));
  if (type.allowsRetroactive) facts.push(t('settings.types.allowsRetroactive'));
  return facts.join(' · ');
}

export function TypesTable() {
  const { t } = useTranslation('timeOff');
  const { data, isLoading, isError } = useTimeOffTypes({ includeInactive: true });
  const deleteType = useDeleteTimeOffType();
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<TimeOffTypeDto | null>(null);

  function openCreate() {
    setEditingType(null);
    setDialogOpen(true);
  }

  function openEdit(type: TimeOffTypeDto) {
    setEditingType(type);
    setDialogOpen(true);
  }

  async function handleDeactivate(type: TimeOffTypeDto) {
    if (!type.id) return;
    const result = await confirm({
      title: t('settings.types.deactivate'),
      description: type.name ?? undefined,
      variant: 'destructive',
    });
    if (!result.confirmed) return;
    deleteType.mutate(type.id);
  }

  if (isLoading) {
    return (
      <div
        className="divide-y divide-border rounded-xl border"
        data-testid="time-off-settings-types-loading-skeleton"
      >
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between gap-3 px-5 py-4">
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="time-off-settings-types-error">
        {t('apiErrors.types.default')}
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="time-off-settings-types-table">
      <Toolbar>
        <Toolbar.Info
          title={t('settings.types.title')}
          description={t('settings.types.description')}
        />
        <Toolbar.Actions>
          <Button onClick={openCreate} data-testid="time-off-settings-types-create-btn">
            <Plus className="size-4" />
            {t('settings.types.create')}
          </Button>
        </Toolbar.Actions>
      </Toolbar>

      <div className="divide-y divide-border rounded-xl border bg-card">
        {(data ?? []).map((type) => {
          const facts = buildFactsLine(type, t);

          return (
            <div
              key={type.id}
              className="flex items-center justify-between gap-3 py-4 pr-4 pl-5"
              data-testid={`time-off-type-row-${type.id}`}
            >
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="flex items-center gap-1.5">
                  <span className="text-[15px] font-medium">{type.name}</span>
                  {type.isSystem && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Lock className="size-3.5 shrink-0 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>{t('settings.types.systemLocked')}</TooltipContent>
                    </Tooltip>
                  )}
                </div>
                {facts && <p className="truncate text-sm text-muted-foreground">{facts}</p>}
              </div>

              <div className="flex shrink-0 items-center gap-3">
                <Badge variant={type.isActive ? 'success' : 'secondary'} className="w-fit">
                  {type.isActive
                    ? t('settings.types.columns.status')
                    : t('settings.types.deactivate')}
                </Badge>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => openEdit(type)}
                      aria-label={t('settings.types.edit')}
                      data-testid={`time-off-type-edit-${type.id}`}
                    >
                      <Pencil className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('settings.types.edit')}</TooltipContent>
                </Tooltip>

                {!type.isSystem && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => handleDeactivate(type)}
                        aria-label={t('settings.types.deactivate')}
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                        data-testid={`time-off-type-deactivate-${type.id}`}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('settings.types.deactivate')}</TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <TypeFormDialog open={dialogOpen} onOpenChange={setDialogOpen} editingType={editingType} />

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(open) => !open && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
}
